import { Drawer } from '@material-ui/core';
import { Editor } from '@tiptap/core';
import clsx from 'clsx';
import { Body, Button, Heading, IconButton } from 'copilot-design-system';
import { Form, Formik } from 'formik';
import { useMemo, useState } from 'react';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import { TextField } from 'src/components/TextField';
import { GetCurrencyPrefix } from 'src/legacy/components/Formatters';
import {
  productDetailSchema,
  ProductDetailsSchema,
} from 'src/legacy/components/Products/ProductDetailPage/EditProductDetail';
import {
  PriceInfo,
  ProductSchema,
  productSchema,
} from 'src/legacy/components/Products/ProductEditPage/ProductEditPage';
import { ProductFileDropzone } from 'src/legacy/components/Products/ProductEditPage/ProductFileDropzone';
import { BillingRichTextEditor } from 'src/routes/billing/components/BillingRichTextEditor';
import { LineItemEditorPricingMode } from 'src/routes/billing/components/LineItemEditorV2/LineItemEditor';
import { PriceTable } from 'src/routes/billing/components/PriceTable';
import { LineItem } from 'src/services/api/invoicesApi';
import {
  PriceType,
  Product,
  useAddProductMutation,
  useGetPricesByPortalQuery,
  useGetProductsQuery,
  useUpdateProductMutation,
} from 'src/services/api/productsApi';
import { SubscriptionInterval } from 'src/types/subscriptions';
import { getPublicImageURL } from 'src/utils/ImageUtils';

interface Props {
  open: boolean;
  onClose: () => void;
  defaultCurrency?: string;
  showRecurringField?: boolean;
  toggleLabel?: string;
  mode: LineItemEditorPricingMode;
  productId?: string;
  formType?: 'new' | 'edit';
  lineItems?: LineItem[];
  onProductSelectInLineItem: (
    product: Product,
    selectedPriceId?: string,
  ) => void;
  selectedInterval?: SubscriptionInterval;
  selectedLineItemPriceId: string;
}
type ProductFormType<Mode> = Mode extends 'edit'
  ? ProductDetailsSchema
  : ProductSchema;

export const CreateProductPopover = ({
  open,
  onClose,
  defaultCurrency,
  showRecurringField = false,
  toggleLabel,
  mode,
  productId = '',
  formType,
  lineItems,
  onProductSelectInLineItem,
  selectedInterval,
  selectedLineItemPriceId,
}: Props) => {
  const [addProduct, { isLoading: isCreatingProductLoading }] =
    useAddProductMutation();
  const [isAddingNewPrice, setIsAddingNewPrice] = useState(false);
  const { data: products } = useGetProductsQuery();
  const [updateProduct, { isLoading: isUpdateProductLoading }] =
    useUpdateProductMutation();
  const { data: portalPrices = [] } = useGetPricesByPortalQuery();
  const selectedProduct = products?.find((product) => product.id === productId);
  const [latestPriceId, setLatestPriceId] = useState<string | undefined>(
    undefined,
  );

  const selectedProductPrices = useMemo(
    () => portalPrices.filter((price) => price.productId === productId),
    [portalPrices, productId],
  );
  const currencyPrefix = GetCurrencyPrefix({ currency: defaultCurrency || '' });

  const initialValues: ProductSchema = useMemo(() => {
    const base = {
      id: selectedProduct?.id ?? '',
      name: selectedProduct?.name ?? '',
      description: selectedProduct?.description ?? '<p></p>',
      imageUrls: selectedProduct?.imageUrls ?? [],
    };
    const basePrices = {
      amount: '',
      currency: defaultCurrency || '',
    };
    if (productId) {
      return {
        ...base,
        prices: [],
      };
    } else {
      if (showRecurringField) {
        return {
          ...base,
          prices: [
            {
              ...basePrices,
              type: PriceType.Recurring,
              interval: selectedInterval || SubscriptionInterval.Month,
            },
          ],
        };
      } else {
        return {
          ...base,
          prices: [
            {
              ...basePrices,
              type: PriceType.OneTime,
            },
          ],
        };
      }
    }
  }, [
    defaultCurrency,
    showRecurringField,
    selectedProduct,
    productId,
    selectedInterval,
  ]);

  const handleSaveProduct = async (data: ProductSchema) => {
    if (formType === 'edit' && productId) {
      const payload: ProductFormType<'edit'> = {
        ...data,
        id: productId,
      };
      const result = await updateProduct({
        payload,
      });
      if (!('error' in result)) {
        onProductSelectInLineItem(result.data, latestPriceId);
        handleCloseProductPopover();
      }
    } else {
      const payload: ProductFormType<'new'> = {
        ...data,
      };
      const result = await addProduct({
        product: payload,
      });
      if (!('error' in result)) {
        onProductSelectInLineItem(result.data);
        handleCloseProductPopover();
      }
    }
  };

  const handleCloseProductPopover = () => {
    onClose();
    setIsAddingNewPrice(false);
    setLatestPriceId(undefined);
  };

  const schema = useMemo(() => {
    if (productId) {
      return toFormikValidationSchema(productDetailSchema);
    } else {
      return toFormikValidationSchema(productSchema(currencyPrefix));
    }
  }, [productId, currencyPrefix]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleCloseProductPopover}
      variant="temporary"
      classes={{
        paper: 'max-w-[600px] w-full h-full flex flex-col',
      }}
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="h-[60px] border-solid border-b border-t-0 border-x-0 border-non-hover-border flex justify-between items-center px-6">
          <Heading size="xl">
            {productId ? 'Edit product' : 'Create product'}
          </Heading>
          <IconButton
            icon="Close"
            onClick={handleCloseProductPopover}
            variant="minimal"
          />
        </div>

        <Formik
          initialValues={initialValues}
          onSubmit={handleSaveProduct}
          validationSchema={schema}
          validateOnMount={true}
        >
          {({
            values,
            errors,
            handleBlur,
            touched,
            handleChange,
            setFieldValue,
            handleSubmit,
            dirty,
          }) => {
            const handleContentChange = (editor: Editor) => {
              setFieldValue('description', editor.getHTML());
            };

            return (
              <>
                <Form
                  id="create-product-form"
                  className="flex-1 overflow-auto px-6 py-4 flex flex-col gap-6"
                >
                  <div
                    className={clsx('flex flex-col gap-6', {
                      'opacity-25 pointer-events-none': isAddingNewPrice,
                    })}
                  >
                    <div>
                      <Heading className="text-black-heading mb-1">
                        Name
                      </Heading>
                      <TextField
                        value={values.name}
                        name="name"
                        error={!!(errors.name && touched.name)}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        helperText={
                          errors.name && touched.name ? (
                            errors.name
                          ) : (
                            <Body className="text-[13px] text-gray-small">
                              For optimal search visibility, keep titles less
                              than 60 characters.
                            </Body>
                          )
                        }
                        autoFocus
                        variant="outlined"
                        fullWidth
                        inputProps={{
                          className: 'text-black-heading !h-10 text-base',
                        }}
                      />
                    </div>
                    <div>
                      <div className="flex items-center mb-1">
                        <Heading className="text-black-heading">
                          Description
                        </Heading>
                        <Heading className=" text-gray-small">
                          &nbsp;- Optional
                        </Heading>
                      </div>
                      <div>
                        <div>
                          <BillingRichTextEditor
                            description={values.description || ''}
                            onContentChange={handleContentChange}
                          />
                        </div>
                        {mode !== LineItemEditorPricingMode.All && (
                          <Body className="text-[13px] text-gray-small text-right">
                            For optimal search visibility, keep descriptions
                            less than 155 characters.
                          </Body>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center mb-1">
                        <Heading className="text-black-heading">Image</Heading>
                      </div>
                      <ProductFileDropzone
                        imageUrl={getPublicImageURL(
                          String(values.imageUrls || ''),
                          {
                            resize: { width: 300, height: 300 },
                          },
                        )}
                      />

                      <div className="flex justify-end">
                        <>
                          {errors.imageUrls ? (
                            <Body className="text-red text-[13px] font-normal">
                              {String(errors.imageUrls || '')}
                            </Body>
                          ) : (
                            <Body className=" text-[13px] text-gray-small">
                              {'Recommended at least 448x448 pixels.'}
                            </Body>
                          )}
                        </>
                      </div>
                    </div>
                  </div>
                  {productId ? (
                    <PriceTable
                      productId={productId}
                      defaultCurrency={defaultCurrency || ''}
                      mode={mode}
                      prices={selectedProductPrices}
                      isAddingNewPrice={isAddingNewPrice}
                      onAddNewPrice={setIsAddingNewPrice}
                      lineItems={lineItems}
                      onSetPriceId={(priceId) => setLatestPriceId(priceId)}
                      selectedInterval={selectedInterval}
                      selectedPriceId={latestPriceId ?? selectedLineItemPriceId}
                    />
                  ) : (
                    <PriceInfo
                      defaultCurrency={defaultCurrency || ''}
                      showRecurringField={
                        showRecurringField ||
                        mode === LineItemEditorPricingMode.All
                      }
                      toggleLabel={toggleLabel}
                      toggleShouldBeShown={
                        mode === LineItemEditorPricingMode.All
                      }
                      size="lg"
                      selectedInterval={selectedInterval}
                    />
                  )}
                </Form>
                {/* Footer */}
                <div className="h-[60px] border-solid border-t border-b-0 border-x-0 border-non-hover-border flex justify-start items-center px-6 gap-4">
                  <Button
                    label={productId ? 'Save' : 'Create'}
                    variant="primary"
                    onClick={() => {
                      if (dirty || latestPriceId) {
                        handleSubmit();
                      } else {
                        handleCloseProductPopover();
                      }
                    }}
                    type="button"
                    loading={isCreatingProductLoading || isUpdateProductLoading}
                    disabled={isAddingNewPrice}
                  />
                  <Button
                    label={productId ? 'Cancel' : 'Discard'}
                    variant="secondary"
                    onClick={handleCloseProductPopover}
                  />
                </div>
              </>
            );
          }}
        </Formik>
      </div>
    </Drawer>
  );
};
